import { useEffect, useRef, useState } from "react";
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, UserPlus, Users, X, Check, MessageCircle, Send } from "lucide-react";
import { useCall } from "@/context/CallContext";
import { playIncomingRing } from "@/lib/ringtones";
import api from "@/lib/api";

function Tile({ stream, name, muted, isLocal }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && stream) ref.current.srcObject = stream; }, [stream]);
  const hasVideo = stream && stream.getVideoTracks?.().some((t) => t.enabled && t.readyState === "live");
  return (
    <div className="relative rounded-2xl overflow-hidden bg-black/60 aspect-video grid place-items-center" data-testid={`group-tile-${isLocal ? "self" : name}`}>
      <video ref={ref} autoPlay playsInline muted={muted} className={`w-full h-full object-cover ${hasVideo ? "" : "hidden"}`} />
      {!hasVideo && (
        <div className="w-16 h-16 rounded-full bg-pula-green/30 grid place-items-center font-heading font-extrabold text-2xl text-white">
          {name?.[0]?.toUpperCase() || "?"}
        </div>
      )}
      <span className="absolute bottom-2 left-2 text-xs font-body text-white bg-black/50 rounded-full px-2 py-0.5">{isLocal ? "You" : name}</span>
    </div>
  );
}

export function GroupCallOverlay() {
  const {
    groupState, groupIncoming, groupParticipants, groupStreams, groupLocalStream,
    groupMuted, groupCameraOff, groupTitle, acceptGroup, declineGroup, leaveGroup,
    toggleGroupMute, toggleGroupCamera, inviteToGroup,
    groupChatMessages, groupTypingName, sendGroupChat, sendGroupTyping,
  } = useCall();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [friends, setFriends] = useState([]);
  const ringStopRef = useRef(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatDraft, setChatDraft] = useState("");
  const [chatSeen, setChatSeen] = useState(0);
  const chatEndRef = useRef(null);
  const typingSendRef = useRef(null);

  useEffect(() => {
    if (chatOpen) { setChatSeen(groupChatMessages.length); chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }
  }, [groupChatMessages, chatOpen]);
  const unreadChat = Math.max(0, groupChatMessages.length - chatSeen);

  const onChatInput = (e) => {
    setChatDraft(e.target.value);
    sendGroupTyping(true);
    if (typingSendRef.current) clearTimeout(typingSendRef.current);
    typingSendRef.current = setTimeout(() => sendGroupTyping(false), 1800);
  };
  const submitChat = (e) => {
    e?.preventDefault?.();
    const t = chatDraft.trim();
    if (!t) return;
    sendGroupChat(t);
    setChatDraft("");
    if (typingSendRef.current) clearTimeout(typingSendRef.current);
    sendGroupTyping(false);
  };

  useEffect(() => {
    if (pickerOpen) api.get("/friends").then(({ data }) => setFriends(data.friends || [])).catch(() => {});
  }, [pickerOpen]);

  // Ring the member's chosen ringtone while a group invite is pending.
  useEffect(() => {
    if (groupState === "incoming") { ringStopRef.current = playIncomingRing(); }
    return () => { if (ringStopRef.current) { ringStopRef.current(); ringStopRef.current = null; } };
  }, [groupState]);

  if (groupState === "incoming" && groupIncoming) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/70 grid place-items-center p-4" data-testid="group-incoming">
        <div className="pula-card p-6 max-w-sm w-full text-center bg-white">
          <Users className="w-10 h-10 text-pula-green mx-auto mb-2" />
          <p className="font-heading font-extrabold text-xl text-pula-ink">{groupIncoming.from_name} is starting a group call</p>
          {groupIncoming.title ? <p className="font-body text-pula-green text-sm mt-1 font-semibold" data-testid="group-incoming-title">"{groupIncoming.title}"</p> : null}
          <p className="font-body text-pula-inksoft text-sm mt-1">{(groupIncoming.media === "audio") ? "Voice" : "Video"} call · up to 4 people</p>
          <div className="flex gap-3 justify-center mt-5">
            <button data-testid="group-decline" onClick={declineGroup} className="inline-flex items-center gap-2 rounded-full bg-pula-accent/10 text-pula-accent px-5 py-2.5 font-body"><X className="w-4 h-4" /> Decline</button>
            <button data-testid="group-accept" onClick={acceptGroup} className="inline-flex items-center gap-2 rounded-full bg-pula-green text-white px-5 py-2.5 font-body"><Check className="w-4 h-4" /> Join</button>
          </div>
        </div>
      </div>
    );
  }

  if (groupState !== "active") return null;

  const remoteIds = groupParticipants.map((p) => p.user_id);
  const inCallIds = new Set(remoteIds);
  const addable = friends.filter((f) => !inCallIds.has(f.user_id));
  const total = groupParticipants.length + 1;

  return (
    <div className="fixed inset-0 z-[60] bg-pula-ink/95 flex flex-col" data-testid="group-call-overlay">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="font-heading font-bold flex items-center gap-2 min-w-0">
          <Users className="w-5 h-5 shrink-0" />
          <span className="truncate" data-testid="group-title">{groupTitle || "Group call"}</span>
          <span className="text-white/60 font-body text-sm shrink-0">· {total}/4</span>
        </span>
        <button data-testid="group-add-toggle" onClick={() => setPickerOpen((v) => !v)} disabled={total >= 4}
          className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm disabled:opacity-40">
          <UserPlus className="w-4 h-4" /> Add people
        </button>
      </div>

      <div className="flex-1 overflow-auto px-4">
        <div className={`grid gap-3 ${total <= 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-2"} max-w-4xl mx-auto`}>
          <Tile stream={groupLocalStream} name="You" muted isLocal />
          {groupParticipants.map((p) => (
            <Tile key={p.user_id} stream={groupStreams[p.user_id]} name={p.name} muted={false} />
          ))}
        </div>
      </div>

      {pickerOpen && (
        <div className="absolute right-4 top-14 w-72 max-h-80 overflow-auto pula-card bg-white p-3 z-[61]" data-testid="group-add-picker">
          <p className="font-heading font-bold text-pula-ink text-sm mb-2">Add a connection</p>
          {addable.length === 0 ? <p className="text-xs text-pula-inksoft font-body">No more connections to add.</p> : addable.map((f) => (
            <button key={f.user_id} data-testid={`group-add-${f.user_id}`} onClick={() => { inviteToGroup(f); setPickerOpen(false); }}
              className="w-full flex items-center gap-2 rounded-lg px-2 py-2 hover:bg-black/5 text-left">
              <span className="w-8 h-8 rounded-full bg-pula-green/15 grid place-items-center font-heading font-bold text-pula-green">{f.name?.[0]?.toUpperCase()}</span>
              <span className="font-body text-sm text-pula-ink truncate">{f.name}</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center justify-center gap-3 py-5">
        <button data-testid="group-mute" onClick={toggleGroupMute} className={`w-12 h-12 rounded-full grid place-items-center ${groupMuted ? "bg-white text-pula-ink" : "bg-white/15 text-white"}`}>
          {groupMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </button>
        <button data-testid="group-camera" onClick={toggleGroupCamera} className={`w-12 h-12 rounded-full grid place-items-center ${groupCameraOff ? "bg-white text-pula-ink" : "bg-white/15 text-white"}`}>
          {groupCameraOff ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
        </button>
        <button data-testid="group-chat-btn" onClick={() => setChatOpen((v) => !v)} className={`relative w-12 h-12 rounded-full grid place-items-center ${chatOpen ? "bg-pula-green text-white" : "bg-white/15 text-white"}`}>
          <MessageCircle className="w-5 h-5" />
          {unreadChat > 0 && !chatOpen && (
            <span data-testid="group-chat-unread" className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full bg-pula-accent text-white text-[11px] font-bold grid place-items-center">{unreadChat}</span>
          )}
        </button>
        <button data-testid="group-leave" onClick={leaveGroup} className="w-14 h-12 rounded-full bg-pula-accent text-white grid place-items-center">
          <PhoneOff className="w-5 h-5" />
        </button>
      </div>

      {chatOpen && (
        <div data-testid="group-chat-panel" className="absolute bottom-24 left-1/2 -translate-x-1/2 w-[92%] max-w-md bg-pula-ink/95 border border-white/15 rounded-2xl shadow-2xl z-[61] flex flex-col overflow-hidden" style={{ maxHeight: "50vh" }}>
          <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
            <span className="font-heading font-bold text-white text-sm flex items-center gap-2"><MessageCircle className="w-4 h-4 text-pula-green" /> Group chat</span>
            <button data-testid="group-chat-close" onClick={() => setChatOpen(false)} className="text-white/50 hover:text-white text-xs font-body">Close</button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 min-h-[80px]">
            {groupChatMessages.length === 0 ? (
              <p className="text-white/40 text-xs font-body text-center py-4">Type here so everyone can read it, even if the audio drops.</p>
            ) : groupChatMessages.map((m, i) => (
              <div key={i} data-testid="group-chat-message" className={`flex flex-col ${m.mine ? "items-end" : "items-start"}`}>
                {!m.mine && <span className="text-[10px] text-white/45 font-body mb-0.5 px-1">{m.from_name}</span>}
                <span className={`inline-block max-w-[80%] rounded-2xl px-3 py-1.5 text-sm font-body break-words ${m.mine ? "bg-pula-green text-white rounded-br-sm" : "bg-white/15 text-white rounded-bl-sm"}`}>{m.text}</span>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          {groupTypingName && (
            <div data-testid="group-chat-typing" className="px-4 pb-1 text-white/50 text-xs font-body">{groupTypingName} is typing…</div>
          )}
          <form onSubmit={submitChat} className="p-2.5 border-t border-white/10 flex items-center gap-2">
            <input data-testid="group-chat-input" value={chatDraft} onChange={onChatInput} placeholder="Message the group…" autoComplete="off"
              className="flex-1 bg-white/10 text-white placeholder-white/40 rounded-full px-4 py-2 text-sm font-body outline-none focus:bg-white/15" />
            <button data-testid="group-chat-send" type="submit" disabled={!chatDraft.trim()} className="w-10 h-10 rounded-full bg-pula-green grid place-items-center text-white disabled:opacity-40 shrink-0">
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
